import { createRouter, createWebHistory } from 'vue-router'
import { routes } from './routes'
import { useSessionStore } from '@/stores/session.store'
import { SCOPES } from '@/constants/permissions'
import { i18n } from '@/i18n'

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, saved) {
    if (saved) return saved
    if (to.hash) return { el: to.hash, behavior: 'smooth' }
    return { top: 0 }
  },
})

/**
 * Ruta de entrada por defecto según lo que el usuario puede ver.
 * Un usuario de plataforma aterriza en `/platform`; un cliente, en `/app`.
 */
export function landingRoute(session) {
  if (session.isPlatform && !session.isImpersonating) {
    return { name: 'platform-clients' }
  }
  const firstVisible = visibleRoutesFor(session)[0]
  return firstVisible ? { name: firstVisible.name } : { name: 'app-home' }
}

/**
 * Rutas de menú que el usuario puede ver, en su ámbito efectivo.
 * Fuente única del menú lateral: se deriva de `meta.permission` y de los
 * permisos de `/auth/me`, nunca de una lista escrita a mano.
 */
export function visibleRoutesFor(session, scope = session.effectiveScope) {
  return routes
    .filter((route) => route.meta?.scope === scope)
    .flatMap((route) => route.children ?? [])
    .filter((child) => child.meta?.nav && session.can(child.meta.permission))
    .sort((a, b) => (a.meta.nav.order ?? 0) - (b.meta.nav.order ?? 0))
}

/* ─── Guardas ─────────────────────────────────────────────────────────────── */

router.beforeEach(async (to) => {
  const session = useSessionStore()

  // El arranque canjea la cookie de refresco por un token; hasta que termina no
  // se puede decidir nada. Es una sola vez por carga de página.
  if (!session.bootstrapped) await session.bootstrap()

  const isPublic = to.matched.some((record) => record.meta?.public)

  if (!session.isAuthenticated) {
    if (isPublic) return true
    return { name: 'login', query: to.fullPath === '/' ? {} : { redirect: to.fullPath } }
  }

  // Ya autenticado: las pantallas de acceso no tienen sentido.
  if (isPublic && to.name !== 'not-found') return landingRoute(session)

  const scope = to.matched.find((record) => record.meta?.scope)?.meta.scope
  if (scope && !hasScope(session, scope)) return landingRoute(session)

  const permission = to.meta?.permission
  if (permission && !session.can(permission)) {
    // Sin permiso no se redirige en silencio a otra sección: se explica por qué
    // no se puede entrar. Redirigir sin decir nada convierte un problema de
    // permisos en un "el panel está roto".
    return { name: 'forbidden', query: { from: to.fullPath } }
  }

  return true
})

function hasScope(session, scope) {
  if (scope === SCOPES.PLATFORM) return session.isPlatform && !session.isImpersonating
  // La superficie del cliente es accesible por un usuario de organización y por
  // un usuario de plataforma que esté suplantando.
  return !session.isPlatform || session.isImpersonating
}

router.afterEach((to) => {
  const navKey = to.meta?.nav?.key
  const title = navKey ? i18n.global.t(navKey) : i18n.global.t('common.appName')
  document.title = navKey ? `${title} · ${i18n.global.t('common.appName')}` : title
})

export default router
